import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  TextField,
  MenuItem,
  ListSubheader,
  FormControlLabel,
  Checkbox,
  Switch,
  Box,
  Grid,
  IconButton,
  Select,
  FormControl,
  InputLabel,
  OutlinedInput,
  Chip,
} from '@material-ui/core';
import { Close as CloseIcon } from '@material-ui/icons';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { TabletButton } from '@campfire/tablet-button';
import { NotificationConfig } from './NotificationConfigTable';
import { useCampfireTheme } from '../../../../../theme/useCampfireTheme';
import { ManagerSelector } from './ManagerSelector';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    dialogTitle: {
      backgroundColor: theme.color.grey.neutralBrand50,
      borderBottom: `1px solid ${theme.color.grey.border}`,
      padding: '16px 24px 20px 24px',
    },
    dialogContent: {
      padding: '12px 24px 16px 24px',
      minHeight: '400px',
      minWidth: '500px',
    },
    formSection: {
      marginBottom: '24px',
    },
    sectionTitle: {
      fontSize: '14px',
      fontWeight: 600,
      color: theme.color.grey.neutralBrand800,
      marginBottom: '12px',
    },
    textField: {
      marginBottom: '16px',
    },
    switchContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '8px 0',
    },
    helperText: {
      fontSize: '12px',
      color: theme.color.grey.neutralBrand500,
      marginTop: '4px',
    },
    dialogActions: {
      padding: '12px 24px',
      backgroundColor: theme.color.grey.neutralBrand50,
      borderTop: `1px solid ${theme.color.grey.border}`,
    },
    saveButton: {
      minWidth: '100px',
    },
    cancelButton: {
      marginRight: '12px',
    },
    checkbox: {
      color: theme.palette.primary.main,
      '&.Mui-checked': {
        color: theme.palette.primary.main,
      },
    },
  })
);

const NOTIFICATION_TYPES = [
  {
    category: 'Rosters',
    options: [
      { value: 'flexible-roster-template-created', label: 'Roster Template - Date Added' },
      { value: 'volunteer-declined-rostering', label: 'Attendance Reminder Response - "No"' },
      { value: 'volunteer-accepted-rostering', label: 'Attendance Reminder Response - "Yes"' },
      { value: 'volunteer-unavailability-updated', label: 'Unavailability Period - All Submissions' },
      { value: 'volunteer-unavailability-conflict', label: 'Unavailability Period - Conflicts Only' },
      { value: 'activity-unavailability-submitted', label: 'Unavailability Date Submitted' },
      { value: 'activity-availability-submitted', label: 'Availability Submitted' },
    ],
  },
  {
    category: 'Volunteers',
    options: [
      { value: 'application-submitted', label: 'New Incoming Application' },
      { value: 'volunteer-self-exited', label: 'Volunteer Self Exited' },
    ],
  },
  {
    category: 'Role Applications',
    options: [
      { value: 'role-application-submitted', label: 'New Role Application' },
      { value: 'role-application-tasks-completed', label: 'Role Application - Tasks Completed' },
      { value: 'role-application-withdrawn', label: 'Role Application - Withdrawn' },
    ],
  },
  {
    category: 'Activities',
    options: [
      { value: 'new-activity-application', label: 'New Activity Application' },
      { value: 'new-activity-waitlisting', label: 'New Activity Waitlisting' },
      { value: 'new-activity-enrolment-manager-alert', label: 'New Activity Enrolment' },
    ],
  },
  {
    category: 'Incidents',
    options: [
      { value: 'new-incident-report', label: 'New Incident Report' },
      { value: 'new-incident-report-comment', label: 'New Incident Report Comment' },
    ],
  },
  {
    category: 'Reports',
    options: [
      { value: 'ad-hoc-report-submitted', label: 'Ad-Hoc Report Submitted' },
      { value: 'activity-report-created', label: 'Activity Report Created' },
    ],
  },
  {
    category: 'Tasks',
    options: [{ value: 'task-expired-manager-alert', label: 'Task Expired Manager Alert' }],
  },
  {
    category: 'Check-Ins',
    options: [
      { value: 'volunteer-checked-in', label: 'Volunteer Checked In' },
      { value: 'volunteer-checked-out', label: 'Volunteer Checked Out' },
    ],
  },
];

const RECIPIENT_OPTIONS = [
  { field: 'notifyAdmins', label: 'All Admins' },
  { field: 'notifyProgramManagers', label: 'Program Managers' },
  { field: 'notifyActivityLeaders', label: 'Activity Leaders' },
  { field: 'notifySpecificManagers', label: 'Specific Managers' },
  { field: 'notifyCustomEmail', label: 'Custom Email Addresses' },
];

interface NotificationConfigModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (configData: any) => Promise<void>;
  editingConfig: NotificationConfig | null;
  loading?: boolean;
}

interface FormData {
  notificationType: string;
  notifyAdmins: boolean;
  notifyProgramManagers: boolean;
  notifyActivityLeaders: boolean;
  notifySpecificManagers: boolean;
  notifyCustomEmail: boolean;
  customEmail: string;
  selectedManagerIds: string[];
  isEnabled: boolean;
}

const NotificationConfigModal: React.FC<NotificationConfigModalProps> = ({
  open,
  onClose,
  onSave,
  editingConfig,
  loading = false,
}) => {
  const classes = useStyles();
  const { theme } = useCampfireTheme();

  const [formData, setFormData] = useState<FormData>({
    notificationType: '',
    notifyAdmins: false,
    notifyProgramManagers: false,
    notifyActivityLeaders: false,
    notifySpecificManagers: false,
    notifyCustomEmail: false,
    customEmail: '',
    selectedManagerIds: [],
    isEnabled: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when modal opens/closes or config changes
  useEffect(() => {
    if (open) {
      if (editingConfig) {
        setFormData({
          notificationType: editingConfig.notificationType || '',
          notifyAdmins: editingConfig.notifyAdmins || false,
          notifyProgramManagers: editingConfig.notifyProgramManagers || false,
          notifyActivityLeaders: editingConfig.notifyActivityLeaders || false,
          notifySpecificManagers: editingConfig.notifySpecificManagers || false,
          notifyCustomEmail: !!editingConfig.customEmail,
          customEmail: editingConfig.customEmail || '',
          selectedManagerIds: editingConfig.managers?.map((m) => m.managerId) || [],
          isEnabled: editingConfig.isEnabled !== false,
        });
      } else {
        setFormData({
          notificationType: '',
          notifyAdmins: false,
          notifyProgramManagers: false,
          notifyActivityLeaders: false,
          notifySpecificManagers: false,
          notifyCustomEmail: false,
          customEmail: '',
          selectedManagerIds: [],
          isEnabled: true,
        });
      }
      setErrors({});
    }
  }, [open, editingConfig]);

  const getModalTitle = () => {
    return editingConfig ? 'Edit' : 'Add New';
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.notificationType) {
      newErrors.notificationType = 'Notification type is required';
    }

    const hasRecipients =
      formData.notifyAdmins ||
      formData.notifyProgramManagers ||
      formData.notifyActivityLeaders ||
      formData.notifySpecificManagers ||
      formData.notifyCustomEmail;

    if (!hasRecipients) {
      newErrors.recipients = 'At least one recipient type must be selected or custom email provided';
    }

    if (formData.notifySpecificManagers && formData.selectedManagerIds.length === 0) {
      newErrors.selectedManagerIds = 'At least one manager must be selected when specific managers is enabled';
    }

    if (formData.notifyCustomEmail && !formData.customEmail.trim()) {
      newErrors.customEmail = 'Custom email addresses are required when custom email is selected';
    }

    if (formData.notifyCustomEmail && formData.customEmail) {
      const emails = formData.customEmail
        .split(',')
        .map((email) => email.trim())
        .filter((email) => email);
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (emails.length === 0) {
        newErrors.customEmail = 'At least one valid email address is required';
      } else {
        const invalidEmails = emails.filter((email) => !emailRegex.test(email));
        if (invalidEmails.length > 0) {
          newErrors.customEmail = `Invalid email address${invalidEmails.length > 1 ? 'es' : ''}: ${invalidEmails.join(
            ', '
          )}`;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const configData = {
      notificationType: formData.notificationType,
      notifyAdmins: formData.notifyAdmins,
      notifyProgramManagers: formData.notifyProgramManagers,
      notifyActivityLeaders: formData.notifyActivityLeaders,
      notifySpecificManagers: formData.notifySpecificManagers,
      customEmails:
        formData.notifyCustomEmail && formData.customEmail
          ? formData.customEmail
              .split(',')
              .map((email) => email.trim())
              .filter((email) => email)
          : [],
      managerIds: formData.notifySpecificManagers ? formData.selectedManagerIds : [],
      isEnabled: formData.isEnabled,
    };

    if (editingConfig) {
      onSave({
        ...configData,
        id: editingConfig.notificationConfigId,
      });
    } else {
      onSave(configData);
    }
  };

  const handleFieldChange = (field: keyof FormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle style={{ paddingBottom: 0 }}>
        <Grid container justify='space-between'>
          <Grid item>
            <Box alignItems='center'>
              <Typography
                style={{
                  fontSize: '23px',
                  fontWeight: 550,
                  marginRight: '16px',
                  color: theme.color.grey.neutral400,
                  paddingLeft: '5px',
                }}
              >
                {getModalTitle()}
              </Typography>
            </Box>
          </Grid>
          <Grid item>
            <Box display='flex' alignItems='center'>
              <IconButton key='close' aria-label='Close' color='inherit' onClick={onClose}>
                <CloseIcon color='action' />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
        <Box pr={3} mt={1}>
          <Typography style={{ fontSize: 14, paddingLeft: '5px', paddingRight: '10px' }} color='textSecondary'>
            Configure who gets notified for important events and activities in your organisation.
            <br /> Hit &apos;Cancel&apos; to close this window.
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Box display='flex' flexDirection='column' style={{ gap: '16px' }}>
          <Box>
            <Typography
              style={{ fontSize: '14px', fontWeight: 500, marginBottom: '12px', color: theme.color.grey.neutral400 }}
            >
              Notification Type
            </Typography>
            <TextField
              select
              fullWidth
              value={formData.notificationType}
              onChange={(e) => handleFieldChange('notificationType', e.target.value)}
              variant='outlined'
              className={classes.textField}
              disabled={loading}
              error={!!errors.notificationType}
              helperText={errors.notificationType}
            >
              {NOTIFICATION_TYPES.map((category) => [
                <ListSubheader key={category.category}>{category.category}</ListSubheader>,
                ...category.options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                )),
              ])}
            </TextField>
          </Box>

          <Box>
            <Typography
              style={{ fontSize: '14px', fontWeight: 500, marginBottom: '12px', color: theme.color.grey.neutral400 }}
            >
              Recipients
            </Typography>
            <FormControl variant='outlined' fullWidth className={classes.textField} error={!!errors.recipients}>
              <InputLabel>Select Recipients</InputLabel>
              <Select
                multiple
                value={RECIPIENT_OPTIONS.filter((option) => formData[option.field as keyof FormData]).map(
                  (option) => option.field
                )}
                onChange={(e) => {
                  const selectedFields = e.target.value as string[];
                  RECIPIENT_OPTIONS.forEach((option) => {
                    handleFieldChange(option.field as keyof FormData, selectedFields.includes(option.field));
                  });
                }}
                input={<OutlinedInput label='Select Recipients' />}
                renderValue={(selected) => (
                  <Box display='flex' flexWrap='wrap' style={{ gap: '4px' }}>
                    {(selected as string[]).map((field) => {
                      const option = RECIPIENT_OPTIONS.find((opt) => opt.field === field);
                      return <Chip key={field} label={option?.label} size='small' />;
                    })}
                  </Box>
                )}
                disabled={loading}
              >
                {RECIPIENT_OPTIONS.map((option) => (
                  <MenuItem key={option.field} value={option.field}>
                    <Checkbox
                      checked={formData[option.field as keyof FormData] as boolean}
                      className={classes.checkbox}
                    />
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.recipients && (
                <Typography variant='caption' color='error' style={{ display: 'block', marginTop: '4px' }}>
                  {errors.recipients}
                </Typography>
              )}
            </FormControl>

            {formData.notifyCustomEmail && (
              <TextField
                fullWidth
                label='Custom Email Addresses'
                value={formData.customEmail}
                onChange={(e) => handleFieldChange('customEmail', e.target.value)}
                variant='outlined'
                className={classes.textField}
                disabled={loading}
                error={!!errors.customEmail}
                helperText={errors.customEmail || 'Separate multiple email addresses with commas'}
                placeholder='admin@example.com, manager@example.com'
                multiline
                rows={2}
              />
            )}

            {formData.notifySpecificManagers && (
              <Box>
                <Typography
                  style={{
                    fontSize: '14px',
                    fontWeight: 500,
                    marginBottom: '12px',
                    color: theme.color.grey.neutral400,
                  }}
                >
                  Select Managers
                </Typography>
                <ManagerSelector
                  selectedManagerIds={formData.selectedManagerIds}
                  onChange={(managerIds) => handleFieldChange('selectedManagerIds', managerIds)}
                  error={errors.selectedManagerIds}
                  disabled={loading}
                />
              </Box>
            )}
          </Box>

          <Box display='flex' justifyContent='space-between' alignItems='center' style={{ padding: '16px 0' }}>
            <Box>
              <Typography style={{ fontSize: '14px', fontWeight: 500, color: theme.color.grey.neutral400 }}>
                Enable Notification
              </Typography>
              <Typography style={{ fontSize: '13px', color: theme.color.grey.neutral300 }}>
                When enabled, this notification will be sent automatically based on your configuration.
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isEnabled}
                  onChange={(e) => handleFieldChange('isEnabled', e.target.checked)}
                  color='primary'
                  disabled={loading}
                />
              }
              label=''
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Box padding={2}>
          <TabletButton variant='text' color='error' onClick={onClose} disabled={loading} style={{ marginRight: 8 }}>
            Cancel
          </TabletButton>
          <TabletButton variant='contained' color='primary' onClick={handleSubmit} disabled={loading}>
            {loading ? 'Saving...' : editingConfig ? 'Update Notification' : 'Add Notification'}
          </TabletButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export { NotificationConfigModal };
